import { useMemo, useState } from 'react';
import { ActionIcon, Tabs, Tooltip, Menu, Text } from '@mantine/core';
import {
  IconChevronUp,
  IconChevronDown,
  IconLogs,
  IconMail,
  IconVariable,
  IconBug,
  IconWorld,
  IconSearch,
  IconDots,
  IconCancel,
  IconSettings,
} from '@tabler/icons-react';
import clsx from 'clsx';
import { cn } from '~/common/string/classname';
import { useAtom } from 'jotai';
import { VariablesPanel } from '../browser/status-panel/panels/VariablesPanel';
import { EmailPanel } from '../browser/status-panel/panels/EmailPanel';
import { BrowserConsolePanel } from '../browser/status-panel/panels/BrowserConsolePanel';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { AdminDebug } from '../browser/status-panel/panels/admin/AdminDebug';
import { NetworkPanel } from '../browser/status-panel/panels/NetworkPanel';
import { useTabOverflow, type TabItem } from '~/hooks/useTabOverflow';
import { adminUiHiddenAtom, consolePanelActiveTabAtom, type ConsolePanelTab } from '~/atoms/atoms';
import { recorderConsoleLogsAtom, recorderNetworkLogsAtom } from '~/atoms/recorder-atoms';
import { useResetAtom } from 'jotai/utils';

const ICON_SIZE = 16;

const tabs: TabItem[] = [
  {
    value: 'variables',
    label: 'Variables',
    icon: IconVariable,
  },
  {
    value: 'settings',
    label: 'Settings',
    icon: IconSettings,
  },
  {
    value: 'mailbox',
    label: 'Mailbox',
    icon: IconMail,
  },
  {
    value: 'network',
    label: 'Network',
    icon: IconWorld,
  },
  {
    value: 'console',
    label: 'Console',
    icon: IconLogs,
  },
];

export function ConsolePanel({
  testContentId,
  isCollapsed,
  onToggleCollapse,
}: {
  testContentId: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const [activeTab, setActiveTab] = useAtom(consolePanelActiveTabAtom);
  const [showSearch, setShowSearch] = useState(false);
  const { isAdmin } = useAuthData();
  const [adminUiHidden] = useAtom(adminUiHiddenAtom);
  const resetConsoleLogs = useResetAtom(recorderConsoleLogsAtom);
  const resetNetworkLogs = useResetAtom(recorderNetworkLogsAtom);

  const allTabs = useMemo(
    () =>
      [
        ...tabs,
        ...(isAdmin && !adminUiHidden ? [{ value: 'admin' as const, label: 'Debug', icon: IconBug }] : []),
      ] satisfies TabItem[],
    [isAdmin, adminUiHidden],
  );

  const { ref: tabsContainerRef, visibleTabs, overflowTabs } = useTabOverflow({ tabs: allTabs });

  return (
    <div className="flex h-full min-h-24 w-full flex-col">
      <Tabs
        value={activeTab}
        onChange={(value) => {
          if (value) {
            setActiveTab(value as ConsolePanelTab);
            // Reset search state when switching tabs
            setShowSearch(false);
          }
        }}
        variant="unstyled"
        className="flex h-full flex-col"
        keepMounted={false}
      >
        <div
          ref={tabsContainerRef}
          className="flex flex-shrink-0 items-center justify-between gap-2 p-1"
        >
          <div className="flex min-w-0 flex-1 items-center">
            <Tabs.List
              onClick={() => {
                if (isCollapsed && onToggleCollapse) {
                  onToggleCollapse();
                }
              }}
              className="flex-shrink-0"
            >
              {visibleTabs.map((tab) => (
                <Tabs.Tab
                  key={tab.value}
                  value={tab.value}
                  leftSection={!!tab.icon && <tab.icon size={ICON_SIZE} />}
                  className={cn(
                    'px-2 py-2 data-[active=true]:rounded-md',
                    tab.value === 'admin' ?
                      'dark:data-[active=true]:bg-[var(--mantine-color-orange-9)]/20 text-[var(--mantine-color-orange-5)] data-[active=true]:bg-[var(--mantine-color-orange-1)] data-[active=true]:text-[var(--mantine-color-orange-6)]'
                    : 'data-[active=true]:bg-[var(--mantine-color-gray-2)] data-[active=true]:text-[var(--mantine-color-gray-9)] dark:data-[active=true]:bg-[var(--mantine-color-gray-8)] dark:data-[active=true]:text-[var(--mantine-color-gray-2)]',
                  )}
                >
                  {tab.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>

            {/* Overflow menu for hidden tabs */}
            {overflowTabs.length > 0 && (
              <Menu
                shadow="md"
                position="bottom-start"
              >
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="md"
                    className="ml-1"
                    onClick={() => {
                      if (isCollapsed && onToggleCollapse) {
                        onToggleCollapse();
                      }
                    }}
                  >
                    <IconDots size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {overflowTabs.map((tab) => (
                    <Menu.Item
                      key={tab.value}
                      leftSection={!!tab.icon && <tab.icon size={ICON_SIZE} />}
                      onClick={() => setActiveTab(tab.value)}
                      className={clsx({
                        'text-[var(--mantine-color-orange-5)]': tab.value === 'admin',
                      })}
                    >
                      <Text
                        size="sm"
                        className={clsx({
                          'text-[var(--mantine-color-orange-5)]': tab.value === 'admin',
                        })}
                      >
                        {tab.label}
                      </Text>
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            )}
          </div>

          <div className="flex flex-shrink-0 items-center gap-1">
            {/* Clear for console and network tabs */}
            {(activeTab === 'console' || activeTab === 'network') && (
              <Tooltip
                label={`Clear ${activeTab === 'console' ? 'logs' : 'requests'}`}
                position="bottom"
                openDelay={300}
              >
                <ActionIcon
                  onClick={() => {
                    if (activeTab === 'console') {
                      resetConsoleLogs();
                    } else if (activeTab === 'network') {
                      resetNetworkLogs();
                    }
                  }}
                  variant="subtle"
                  color="gray"
                >
                  <IconCancel size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            {/* Search works for console, network, and variables tabs */}
            {(activeTab === 'console' || activeTab === 'network' || activeTab === 'variables') && (
              <Tooltip
                label={
                  activeTab === 'console' ? 'Search logs'
                  : activeTab === 'network' ?
                    'Search requests'
                  : 'Search variables'
                }
                position="bottom"
                openDelay={300}
              >
                <ActionIcon
                  onClick={() => {
                    setShowSearch((prev) => !prev);
                  }}
                  variant={showSearch ? 'light' : 'subtle'}
                  color="gray"
                >
                  <IconSearch size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            <ActionIcon
              onClick={() => {
                if (onToggleCollapse) {
                  onToggleCollapse();
                }
              }}
              variant="subtle"
              color="gray"
            >
              {!isCollapsed ?
                <IconChevronDown size={16} />
              : <IconChevronUp size={16} />}
            </ActionIcon>
          </div>
        </div>

        {!isCollapsed && (
          <div className="min-h-0 flex-1 overflow-auto">
            <Tabs.Panel
              value="console"
              className="h-full"
            >
              <BrowserConsolePanel
                showSearch={showSearch}
                setShowSearch={setShowSearch}
              />
            </Tabs.Panel>
            <Tabs.Panel
              value="variables"
              className="h-full"
            >
              <VariablesPanel
                testContentId={testContentId}
                showSearch={showSearch}
                setShowSearch={setShowSearch}
              />
            </Tabs.Panel>
            <Tabs.Panel
              value="settings"
              className="h-full w-2/5 p-4"
            >
              <div className="text-sm text-stone-500">Settings panel - reserved for future settings</div>
            </Tabs.Panel>
            <Tabs.Panel
              value="network"
              className="h-full"
            >
              <NetworkPanel
                showSearch={showSearch}
                setShowSearch={setShowSearch}
              />
            </Tabs.Panel>
            <Tabs.Panel
              value="mailbox"
              className="h-full"
            >
              <EmailPanel />
            </Tabs.Panel>
            {!!isAdmin && (
              <Tabs.Panel
                value="admin"
                className="h-full"
              >
                <AdminDebug />
              </Tabs.Panel>
            )}
          </div>
        )}
      </Tabs>
    </div>
  );
}
